import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { MusicNote, Envelope, Users, Broadcast, ArrowRight, SpotifyLogo, SoundcloudLogo } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const features = [
  {
    icon: MusicNote,
    title: 'Carica i tuoi brani',
    description: 'Upload audio diretto o link da Spotify, SoundCloud e altre piattaforme.',
  },
  {
    icon: Envelope,
    title: 'Promozione automatica',
    description: 'Invia i tuoi brani a radio, locali e organizzatori con un click.',
  },
  {
    icon: Users,
    title: 'Network professionale',
    description: 'Connettiti con organizzatori di eventi sempre alla ricerca di nuovi talenti.',
  },
  {
    icon: Broadcast,
    title: 'Raggiungi nuovi ascoltatori',
    description: 'Fatti scoprire da chi conta nel mondo della musica.',
  },
];

const stats = [
  { value: '500+', label: 'Artisti registrati' },
  { value: '1.2k', label: 'Brani caricati' },
  { value: '300+', label: 'Organizzatori' },
  { value: '5k+', label: 'Email inviate' },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar transparent />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center hero-gradient">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1758200519616-56bac165cb33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxkaiUyMHBsYXlpbmclMjBtdXNpYyUyMGxpdmUlMjBjbHVifGVufDB8fHx8MTc3NDE5NDAxMnww&ixlib=rb-4.1.0&q=85"
            alt="DJ performing"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block text-[#CCFF00] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                IL TUO MANAGER VIRTUALE
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Fai sentire la tua
                <span className="text-[#CCFF00]"> musica</span> al mondo
              </h1>
              <p className="text-lg text-[#A1A1AA] mb-8 max-w-lg leading-relaxed">
                BeatPromoter è la piattaforma che connette musicisti emergenti con radio, locali e organizzatori di eventi. 
                Carica i tuoi brani e lascia che la tua musica raggiunga chi conta.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/auth?mode=register">
                  <Button 
                    size="lg" 
                    className="bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full px-8 font-semibold group"
                    data-testid="hero-cta-musician"
                  >
                    Sono un Musicista
                    <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/auth?mode=register&type=organizer">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 rounded-full px-8"
                    data-testid="hero-cta-organizer"
                  >
                    Sono un Organizzatore
                  </Button>
                </Link>
              </div>

              {/* Platform logos */}
              <div className="flex items-center gap-6 mt-10">
                <span className="text-sm text-[#A1A1AA]">Integrato con:</span>
                <SpotifyLogo size={28} className="text-[#A1A1AA] hover:text-[#1DB954] transition-colors" />
                <SoundcloudLogo size={28} className="text-[#A1A1AA] hover:text-[#FF5500] transition-colors" />
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-4"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="stat-card"
                >
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Come funziona
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Una piattaforma completa per gestire la tua promozione musicale
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card card p-6 rounded-xl"
              >
                <div className="w-12 h-12 rounded-lg bg-[#CCFF00]/10 flex items-center justify-center mb-4">
                  <feature.icon size={24} weight="fill" className="text-[#CCFF00]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* For Organizers Section */}
      <section className="py-24 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FF0055] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">
                PER ORGANIZZATORI
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Scopri nuovi talenti per i tuoi eventi
              </h2>
              <p className="text-[#A1A1AA] mb-8 leading-relaxed">
                Sei un organizzatore di eventi, gestisci un locale o una radio? 
                BeatPromoter ti permette di scoprire artisti emergenti, ascoltare le loro demo 
                e contattarli direttamente per le tue serate.
              </p>
              <ul className="space-y-4 mb-8">
                {['Filtra per genere musicale', 'Ascolta le demo direttamente in app', 'Salva i tuoi artisti preferiti', 'Contatta le band con un click'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                    <div className="w-5 h-5 rounded-full bg-[#FF0055]/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-[#FF0055]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/auth?mode=register&type=organizer">
                <Button className="bg-[#FF0055] text-white hover:bg-[#FF3377] rounded-full px-8">
                  Registrati come Organizzatore
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/2147029/pexels-photo-2147029.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Concert crowd"
                className="rounded-2xl w-full"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Pronto a far crescere la tua musica?
            </h2>
            <p className="text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
              Unisciti a centinaia di artisti che stanno già usando BeatPromoter per promuovere la loro musica.
            </p>
            <Link to="/auth?mode=register">
              <Button 
                size="lg" 
                className="bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full px-12 font-semibold"
                data-testid="cta-register"
              >
                Inizia Gratis
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-[#CCFF00] flex items-center justify-center">
              <MusicNote size={18} weight="fill" className="text-black" />
            </div>
            <span className="font-semibold">BeatPromoter</span>
          </div>
          <p className="text-sm text-[#A1A1AA]">
            © 2024 BeatPromoter. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
}
